import cn from "classnames";
import React, { Fragment, useRef, useEffect } from "react";
import { Transition, Dialog } from "@headlessui/react";
import { useRouter } from "next/router";

interface Props {
    onClose: () => void;
    open: boolean;
    cancelable: boolean;
    size?: "small" | "medium" | "large" | "full";
    withoutBackground?: boolean;
    children?: React.ReactNode;
}

const Drawer: React.FC<Props> = ({
    size,
    open,
    cancelable,
    onClose,
    children,
    withoutBackground,
}) => {
    const { locale } = useRouter();
    const _onClose = () => {
        if (cancelable) onClose();
    };
    const completeRef = useRef(null);

    useEffect(() => {
        if (!open) {
            document.documentElement.style.overflow = 'unset';
        }
        return () => {
            document.documentElement.style.overflow = 'unset';
        };
    }, [open]);

    let sizeClass;
    if (size === "full") {
        sizeClass = "max-w-full";
    } else if (size === "large") {
        sizeClass = "md:max-w-[643px]";
    } else if (size === "medium") {
        sizeClass = "md:max-w-[500px]";
    } else if (size === "small") {
        sizeClass = "md:max-w-[350px]";
    } else {
        sizeClass = "md:max-w-[400px]";
    }

    let className = cn(
        sizeClass,
        locale?.includes("ar-") ? "!font-arabic" : "!font-arabic",
        "fixed right-0 top-0 h-full w-screen bg-stone-50 overflow-y-auto flex flex-col"
    );

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog
                initialFocus={completeRef}
                as="div"
                className="fixed inset-0 z-[58] overflow-y-auto"
                onClose={_onClose}
                aria-label="drawer view"
                aria-labelledby="labeldrawer"
            >
                <div className="h-full relative px-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay
                            className={`fixed inset-0 ${withoutBackground
                                ? "backdrop-brightness-90"
                                : "backdrop-brightness-50"
                                }`}
                        />
                    </Transition.Child>

                    <Transition.Child
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-x-full"
                        enterTo="opacity-100 translate-x-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-x-0"
                        leaveTo="opacity-0 translate-x-full"
                    >
                        <div
                            ref={completeRef}
                            className={className}
                dir={locale?.includes("ar-") ? "rtl" : "ltr"}
                                
                        >
                            {children}
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    );
};

export default Drawer; 